import { useEffect, useState } from "react";
import { motion } from "framer-motion";
import { hapticFeedback } from "../lib/telegram";

interface MintSequenceProps {
  brandName: string;
  /** Last four digits if known; masked otherwise */
  lastFour?: string;
  /** Balance to odometer-roll up to (0 shows $0.00 without a roll) */
  balance?: number;
  stampText?: string;
  caption?: string;
  onComplete?: () => void;
}

/**
 * The Mint — card activation as a witnessed manufacture cycle (DESIGN.md §Motion).
 * print → tear → inflate → stamp → balance roll. ~2.6s, transforms+opacity only,
 * a Telegram haptic on every beat. Unskippable by design.
 */
export const MintSequence = ({
  brandName,
  lastFour,
  balance = 0,
  stampText = "Active",
  caption,
  onComplete,
}: MintSequenceProps) => {
  const [shownBalance, setShownBalance] = useState(0);
  const [minted, setMinted] = useState(false);

  useEffect(() => {
    const timers: ReturnType<typeof setTimeout>[] = [];
    const at = (ms: number, fn: () => void) => timers.push(setTimeout(fn, ms));

    at(0, () => hapticFeedback.light());
    // number-group stamps ride the print feed
    [350, 600, 850, 1100].forEach((t) => at(t, () => hapticFeedback.selectionChanged()));
    at(1400, () => hapticFeedback.heavy());
    at(1900, () => hapticFeedback.success());
    at(2000, () => {
      setMinted(true);
      hapticFeedback.medium();
      // odometer roll: 0 → balance over 600ms
      if (balance > 0) {
        const start = performance.now();
        const roll = (now: number) => {
          const p = Math.min((now - start) / 600, 1);
          setShownBalance(balance * (1 - Math.pow(1 - p, 3)));
          if (p < 1) requestAnimationFrame(roll);
        };
        requestAnimationFrame(roll);
      }
    });
    if (onComplete) at(3400, onComplete);
    return () => timers.forEach(clearTimeout);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const numberGroups = ["••••", "••••", "••••", lastFour || "••••"];

  return (
    <div className="min-h-screen bg-ground flex flex-col items-center px-6">
      {/* The slot */}
      <div className="w-full max-w-sm mt-14">
        <div className="h-2.5 bg-ink rounded-full" />
      </div>

      {/* The card prints out of the slot */}
      <div className="w-full max-w-sm overflow-hidden pt-4 pb-2 px-2">
        <motion.div
          initial={{ y: -230, scale: 1 }}
          animate={{
            y: [-230, -160, -160, -90, -90, -20, -20, 0, 0, 0, 0],
            scale: [1, 1, 1, 1, 1, 1, 1, 1, 0.96, 1.12, 1],
          }}
          transition={{
            duration: 2.0,
            times: [0, 0.15, 0.19, 0.34, 0.38, 0.55, 0.59, 0.72, 0.84, 0.92, 1],
            ease: "easeOut",
          }}
          className="relative mx-auto w-full"
        >
          <div className="card-shimmer rounded-[14px] bg-gradient-to-br from-[#12312C] via-card-obj to-[#071512] text-[#E8F5F2] shadow-[0_18px_40px_rgba(11,31,28,0.35)] p-4 aspect-[1.7/1] flex flex-col justify-between overflow-hidden">
            <div className="flex justify-between items-start">
              <div>
                <p className="font-mono text-[10px] uppercase tracking-[0.12em] text-[#9DBDB6]">
                  Virtual
                </p>
                <p className="text-sm font-semibold">{brandName} Card</p>
              </div>
              <span className="text-sm italic font-bold">VISA</span>
            </div>
            <p className="font-mono text-[15px] tracking-[0.14em] flex gap-3">
              {numberGroups.map((g, i) => (
                <motion.span
                  key={i}
                  initial={{ opacity: 0, scale: 1.05 }}
                  animate={{ opacity: 1, scale: 1 }}
                  transition={{ delay: 0.35 + i * 0.25, duration: 0.12 }}
                >
                  {g}
                </motion.span>
              ))}
            </p>
            <div className="flex justify-between items-end">
              <p className="font-mono text-[10px] uppercase tracking-[0.12em] text-[#9DBDB6]">
                {brandName.toUpperCase()}
              </p>
              <p className="font-mono text-[10px] text-[#9DBDB6]">EXP ••/••</p>
            </div>
          </div>

          {/* The stamp thwacks in */}
          {minted && (
            <motion.span
              initial={{ opacity: 0, scale: 2, rotate: -18 }}
              animate={{ opacity: 1, scale: 1, rotate: -6 }}
              transition={{ duration: 0.12, ease: "easeOut" }}
              className="stamp text-mint bg-ground absolute -top-2 -right-2"
            >
              {stampText}
            </motion.span>
          )}
        </motion.div>
      </div>

      {/* Minting line */}
      <p className="font-mono text-[11px] tracking-[0.08em] text-muted mt-4 uppercase">
        {minted ? `Minted ${new Date().toISOString().slice(11, 19)} UTC` : "Minting"}
        <span className="animate-cursor-blink">▮</span>
      </p>

      {/* Balance lands */}
      {minted && (
        <motion.div
          initial={{ opacity: 0, y: 8 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.25 }}
          className="text-center mt-6"
        >
          {balance > 0 && (
            <p className="money text-[40px] leading-none font-semibold text-ink">
              ${shownBalance.toFixed(2)}
            </p>
          )}
          <p className="font-mono text-[11px] uppercase tracking-[0.1em] text-muted mt-2">
            {caption || "available to spend"}
          </p>
        </motion.div>
      )}
    </div>
  );
};
